/**
 * Normalisasi respons GET /pos/shifts/{id}/devices.
 * Bentuk: `{ success, data: { devices: [{device_id, nickname, device_info}] }, message }`. Endpoint
 * hanya mengembalikan device yang masih aktif pada shift, jadi kehadiran device_id selain
 * milik browser ini = masih aktif. Fallback beberapa pembungkus dijaga.
 */
export const normalizeShiftDevices = (raw) => {
  const list = raw?.data?.data?.devices ?? raw?.data?.devices ?? (Array.isArray(raw) ? raw : [])
  if (!Array.isArray(list)) {
    return []
  }
  return list.map((d) => ({
    device_id: String(d?.device_id ?? ''),
    nickname: d?.nickname ?? null,
    device_info: d?.device_info || null,
  }))
}

/**
 * ID pendek untuk membedakan device bernama sama: 6 karakter terakhir UUID (tanpa `-`),
 * uppercase. Sama dengan kledopos-mobile agar ID di HP & browser bisa dicocokkan.
 */
export const shortDeviceId = (id) =>
  String(id || '')
    .replace(/-/g, '')
    .slice(-6)
    .toUpperCase()

const BROWSERS = [
  ['Edge', /Edg(?:e|A|iOS)?\/(\d+)/],
  ['Opera', /OPR\/(\d+)/],
  ['Samsung Internet', /SamsungBrowser\/(\d+)/],
  ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
  ['Chrome', /(?:Chrome|CriOS)\/(\d+)/],
  ['Safari', /Version\/(\d+).*Safari/],
]

const majorVersion = (version) => Number(String(version || '').split('.')[0]) || 0

const detectBrowser = (userAgent) => {
  const found = BROWSERS.find(([, pattern]) => pattern.test(userAgent))
  return found ? `${found[0]} ${userAgent.match(found[1])[1]}` : ''
}

// Model dari UA hanya ada di browser non-Chromium/WebView lama; Chrome kini mengirim `K`.
const detectModelFromUserAgent = (userAgent) => {
  const model = userAgent.match(/Android [^;)]*; ([^;)]+?)(?: Build\/|;|\))/)?.[1]
  return model && !['K', 'Mobile', 'Tablet', 'wv'].includes(model) ? model : ''
}

const detectOsFromHints = ({ platform, platformVersion }) => {
  const major = majorVersion(platformVersion)
  if (platform === 'Windows') {
    if (major >= 13) {
      return 'Windows 11'
    }
    return major > 0 ? 'Windows 10' : 'Windows'
  }
  if (['Android', 'macOS'].includes(platform) && major > 0) {
    return `${platform} ${major}`
  }
  return platform === 'Chrome OS' ? 'ChromeOS' : platform || ''
}

const detectOsFromUserAgent = (userAgent, maxTouchPoints) => {
  const ios = userAgent.match(/(iPhone|iPad|iPod)[^)]*? OS (\d+)_(\d+)/)
  if (ios) {
    return `${ios[1] === 'iPad' ? 'iPadOS' : 'iOS'} ${ios[2]}.${ios[3]}`
  }
  const android = userAgent.match(/Android (\d+(?:\.\d+)?)/)
  if (android) {
    // UA Chrome yang dibekukan (`Android 10; K`) tidak membawa versi asli
    return /Android 10; K\)/.test(userAgent) ? 'Android' : `Android ${android[1]}`
  }
  if (/Windows/.test(userAgent)) {
    return 'Windows'
  }
  // iPad versi baru mengaku Mac; versi macOS di UA dibekukan (10_15_7), jadi tanpa versi
  if (/Macintosh|Mac OS X/.test(userAgent)) {
    return maxTouchPoints > 1 ? 'iPadOS' : 'macOS'
  }
  if (/CrOS/.test(userAgent)) {
    return 'ChromeOS'
  }
  return /Linux/.test(userAgent) ? 'Linux' : ''
}

/**
 * Info device browser ini untuk `device_info` saat registrasi device POS, mis.
 * `SM-X200 · Android 14 · Chrome 141` atau `Windows 11 · Edge 141`. Chromium membekukan
 * model & versi OS di userAgent, jadi dipakai User-Agent Client Hints bila tersedia;
 * userAgent hanya fallback (Safari/Firefox). Kosong bila tidak terdeteksi.
 */
export const getBrowserDeviceInfo = async () => {
  const { userAgent = '', maxTouchPoints = 0, userAgentData } = navigator
  const hints = await Promise.resolve()
    .then(() => userAgentData?.getHighEntropyValues?.(['model', 'platformVersion']))
    .catch(() => null)

  return [
    hints?.model || detectModelFromUserAgent(userAgent),
    hints?.platform ? detectOsFromHints(hints) : detectOsFromUserAgent(userAgent, maxTouchPoints),
    detectBrowser(userAgent),
  ]
    .filter(Boolean)
    .join(' · ')
}
